import {
  useState,
  useEffect,
  JSXElementConstructor,
  Key,
  ReactElement,
  ReactNode,
  ReactPortal,
} from "react";
import {
  Box,
  Drawer,
  CssBaseline,
  AppBar,
  Toolbar,
  List,
  Typography,
  ListItemButton,
  Collapse,
  SxProps,
  Theme,
  ListItemIcon,
  ListItemText,
  Avatar,
  IconButton,
  Grid,
} from "@mui/material";
import {
  NavLink,
  Outlet,
  useNavigate,
  useLocation,
  To,
} from "react-router-dom";
import isEmpty from "lodash/isEmpty";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useAuth0 } from "@auth0/auth0-react";
import { jwtDecode } from "jwt-decode";

import {
  // useLogoutMutation,
  useRefreshTokenMutation,
  useDeleteCookiesMutation,
  useLazyGetUserByMailQuery,
  useLazyGetReferenceDataDetailsQuery
} from "../services/api";
import { RootState, useAppDispatch, useAppSelector } from "../store";
import {
  login,
  logout as logoutAction,
  setUserDetails,
} from "../store/authReducer";
import { ReactComponent as Home } from "../assets/svg/home.svg";
import { ReactComponent as HealthOrganisations } from "../assets/svg/healthOrg.svg";
import { ReactComponent as Laboratories } from "../assets/svg/Laboratories.svg";
import { ReactComponent as ModuleManagement } from "../assets/svg/user.svg";
import { ReactComponent as Logo } from "../assets/images/Logo.svg";
import { ReactComponent as Nurses } from "../assets/svg/nurses.svg";
import { ReactComponent as Surgeons } from "../assets/svg/surgeons.svg";
import { ReactComponent as Users } from "../assets/svg/user.svg";
import { ReactComponent as OperatingTheatre } from "../assets/svg/operating_theatre.svg";
import { ReactComponent as Logout } from "../assets/svg/logout.svg";
import { ReactComponent as Specialities } from "../assets/svg/specialities.svg"
import { ReactComponent as TestTypes } from "../assets/svg/testTypes.svg"
import { ReactComponent as Procedures } from "../assets/svg/procedures.svg"
import { ReactComponent as Diagrams } from "../assets/svg/diagrams.svg"
import { ReactComponent as OrgansIcon } from "../assets/svg/organ.svg";
import { ReactComponent as SitesIcon } from "../assets/svg/site.svg";
import { ReactComponent as LesionsIcon } from "../assets/svg/lesion.svg";
import { ReactComponent as MetadataIcon } from "../assets/svg/metadata.svg";
import { ReactComponent as Specimens } from "../assets/svg/specimen.svg";
import { ReactComponent as Permissions } from "../assets/svg/permissions.svg"
import { ReactComponent as Audit } from "../assets/svg/audit.svg"
import { ReactComponent as RFDownload } from "../assets/svg/rf_download.svg";
import { ReactComponent as Couriers } from "../assets/svg/couriers.svg";
import { setReferenceData } from "../store/referenceData";
import { DEFAULT_SUPER_ADMIN_ROLE_ID } from "../utils/constants";
import { Loader } from "../components";
import { colors } from "../theme/colors";

const drawerWidth = 300;

const styles: Record<string, SxProps<Theme>> = {
  appBar: {
    width: `calc(100% - ${drawerWidth}px)`,
    ml: `${drawerWidth}px`,
    backgroundColor: colors.white,
    boxShadow: "none",
    borderBottom: `1px solid ${colors.lighterGrey}`,
  },
  header: {
    display: "flex",
    alignItems: "center",
    columnGap: "12px",
  },
  headerIcons: {
    display: "flex",
    alignItems: "center",
    columnGap: "32px",
  },
  formControl: {
    border: "0",
    ".MuiSelect-outlined": {
      paddingLeft: "0",
      paddingRight: "8px !important",
      color: colors.black,
      fontSize: "16px",
      fontWeight: "400",
    },
    ".MuiOutlinedInput-notchedOutline": {
      border: "0",
    },
  },
  sideMenu: {
    color: colors.lightestGrey,
    fontSize: "14px",
    fontWeight: "500",
    lineHeight: "150%",
    margin: "0px",
  },
};

const isAbsolutePath: Record<string, string> = {
  "/users": "User List",
  "/user/add": "Add User",
  "/user/roles": "User Roles",
  "/user/roles/details/": "User Role Details",
  "/user/roles/edit/": "Edit User Role",
};

const superAdminSideNavigationOptions = [
  {
    navItem: "Home",
    id: "home",
    pathLocation: "/home",
    icon: <Home />,
  },
  {
    navItem: "Health Organisations",
    id: "healthOrgs",
    pathLocation: "/health-organisations",
    icon: <HealthOrganisations />,
  },
  {
    navItem: "Laboratories",
    id: "laboratories",
    pathLocation: "/laboratories",
    icon: <Laboratories />,
  },
  {
    navItem: "User Management",
    id: "users",
    pathLocation: "/users",
    icon: <ModuleManagement />,
    subItemMenu: [
      {
        navItem: "User List",
        id: "userlist",
        pathLocation: "/users",
        icon: <></>,
      },
      {
        navItem: "Add User",
        id: "laboratories",
        pathLocation: "/user/add",
        icon: <></>,
      },
      {
        navItem: "User Roles",
        id: "user-roles",
        pathLocation: "/user/roles",
        icon: <></>,
      },
    ],
  },
  {
    navItem: "Surgeons",
    id: "surgeons",
    pathLocation: "/surgeons",
    icon: <Surgeons />,
  },
  {
    navItem: "Specimens",
    id: "specimens",
    pathLocation: "/specimens",
    icon: <Specimens />,
  },
  {
    navItem: "Specialities",
    id: "specialities",
    pathLocation: "/specialities",
    icon: <Specialities />,
  },
  {
    navItem: "Test Types",
    id: "testTypes",
    pathLocation: "/test-types",
    icon: <TestTypes />,
  },
  {
    navItem: "Procedures",
    id: "procedures",
    pathLocation: "/procedures",
    icon: <Procedures />,
  },
  {
    navItem: "Organs",
    id: "organs",
    pathLocation: "/organs",
    icon: <OrgansIcon />,
  },
  {
    navItem: "Sites",
    id: "sites",
    pathLocation: "/sites",
    icon: <SitesIcon />,
  },
  {
    navItem: "Lesions",
    id: "lesions",
    pathLocation: "/lesions",
    icon: <LesionsIcon />,
  },
  {
    navItem: "Metadata",
    id: "metadata",
    pathLocation: "/metadata",
    icon: <MetadataIcon />,
  },
  // {
  //   navItem: "Clinical Diagrams",
  //   id: "clinicalDiagrams",
  //   pathLocation: "/clinical-diagrams",
  //   icon: <Diagrams />,
  // },
  {
    navItem: "Permissions",
    id: "permissions",
    pathLocation: "/module",
    icon: <Permissions />,
  },
  {
    navItem: "Specimen Tracking",
    id: "specimenTracking",
    pathLocation: "/specimen-tracking",
    icon: <Audit />,
  },
];

const hospitalAdminSideNavigationOptions = [
  {
    navItem: "Specimens",
    id: "specimens",
    pathLocation: "/specimens",
    icon: <Laboratories />,
  },
  {
    navItem: "Surgeons",
    id: "surgeons",
    pathLocation: "/surgeons",
    icon: <Surgeons />,
  },
  {
    navItem: "Nurses",
    id: "nurses",
    pathLocation: "/nurses",
    icon: <Nurses />,
  },
  {
    navItem: "Admins",
    id: "admin",
    pathLocation: "/admins",
    icon: <Users />,
  },
  {
    navItem: "Operating Theatres",
    id: "operating-theatres",
    pathLocation: "/operating-theatres",
    icon: <OperatingTheatre />,
  },
];

const laboratoryAdminSideNavigationOptions = [
  {
    navItem: "Specimens",
    id: "specimens",
    pathLocation: "/specimens",
    icon: <Laboratories />,
  },
  {
    navItem: "Discrepant Specimens",
    id: "discrepant-specimens",
    pathLocation: "/discrepant-specimens",
    icon: <Laboratories />,
  },
  {
    navItem: "Admins",
    id: "admin",
    pathLocation: "/admins",
    icon: <Users />,
  },
  {
    navItem: "RF Download",
    id: "rf-download",
    pathLocation: "/rf-download",
    icon: <RFDownload />,
  },
  {
    navItem: "Couriers",
    id: "couriers",
    pathLocation: "/couriers",
    icon: <Couriers />,
  },
];

const BasicLayout = () => {
  // Handles the logout mutation logic.
  // const [logout, logoutResult] = useLogoutMutation();

  // Handles the token refreshing mutation logic.
  const [getNewToken, tokenResult] = useRefreshTokenMutation();

  // Get User Details mutation hook
  const [
    trigger,
    {
      data: userDetails,
      isFetching: isUserDetailsLoading,
      isError: isUserDetailsError,
    },
  ] = useLazyGetUserByMailQuery();
  const [triggerReferenceData, { data: referenceData }] = useLazyGetReferenceDataDetailsQuery();

  const [deleteCookies] = useDeleteCookiesMutation();

  // Provides a function to navigate to different pages within the application.
  const navigate = useNavigate();

  const location = useLocation();
  const { pathname, state } = location;
  const { logout, getAccessTokenSilently, isAuthenticated, isLoading } = useAuth0();

  const [userName, setUserName] = useState("");
  const [currentRouteName, setCurrentRouteName] = useState("");
  const [isHistoryAvailable, setIsHistoryAvailable] = useState(false);
  const [open, setOpen] = useState(false);
  const [sideNavigationMenu, setSideNavigationMenu] = useState<any[]>([]);

  const userMail = useAppSelector((state: RootState) => state.auth.userMail);
  const storedUserDetails = useAppSelector((state: RootState) => state.auth.userDetails);
  const referenceDataState = useAppSelector(
    (state: RootState) => state.referenceData
  );
  const details = userDetails || storedUserDetails;

  // Retrieve token data from redux store auth state
  const { userRole } = useAppSelector(
    (state: RootState) => state.auth
  );

  const dispatch = useAppDispatch();
  // If the profile query errored (network/5xx), it will never resolve `details`
  // — stop treating it as "pending" so the loader clears and the logout button
  // stays reachable instead of trapping the user until AutoLogout fires.
  const isUserDetailsPending = Boolean(
    userMail && isEmpty(details) && !isUserDetailsError
  );
  const showLoader = isUserDetailsLoading || isUserDetailsPending;
  // While the profile is loading and no menu is resolved yet, the drawer shows
  // only a loader — hide the nav list AND the logout button until it settles.
  const isSidebarLoading = showLoader && sideNavigationMenu.length === 0;
  const hasReferenceData = Object.values(referenceDataState).some(
    (value) => Array.isArray(value) && value.length > 0
  );

  const handleLogout = async () => {
    // Clear server/client cookies and wipe the local auth state BEFORE handing
    // off to Auth0's logout — Auth0 navigates the page away, so anything after
    // it races the unload and may never run (leaving tokens in localStorage).
    // Mirrors AutoLogout.hardLogout's ordering.
    try {
      await deleteCookies({}).unwrap();
    } catch (_) {
      // Swallow — logout must still proceed even if cookie deletion fails.
    } finally {
      dispatch(logoutAction());
      logout({
        logoutParams: {
          returnTo: window.location.origin,
        },
      });
    }
  };

  // Function to validate the access token stored in local storage
  const validateToken = async () => {
    try {
      const token: any = await getAccessTokenSilently({
        detailedResponse: true,
        authorizationParams: {
          scope: "openid profile email offline_access",
        },
      });
      if (token) {
        const decodedToken: any = jwtDecode(token.access_token);
        const userRole = decodedToken["https://pegasus.user-info/"].role;
        const userEmail = decodedToken?.["https://pegasus.user-info/"]?.mail;
        const healthOrgId =
          decodedToken?.["https://pegasus.user-info/"]?.health_org_id ?? 0;
        const laboratoryId =
          decodedToken?.["https://pegasus.user-info/"]?.laboratory_id ?? 0;
        dispatch(
          login({
            userRole: userRole[0],
            accessToken: token.access_token,
            refreshToken: "",
            userMail: userEmail,
          })
        );
        dispatch(
          setUserDetails({
            userDetails: {
              health_org_id: healthOrgId,
              laboratory_id: laboratoryId,
            },
          })
        );
      }
    } catch (error) {
      // Do not force logout on silent token errors to avoid logging the user out on refresh.
      console.log("Error fetching access token:", error);
    }
  };

  // Run silent token validation once Auth0 is ready.
  useEffect(() => {
    if (!isAuthenticated || isLoading) return;
    validateToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isLoading]);

  // Fetch user details when we have a mail.
  useEffect(() => {
    if (userMail) {
      trigger(userMail);
    }
  }, [userMail, trigger]);

  useEffect(() => {
    // Super admin is identified by the numeric role id (the same signal
    // RequireSuperAdmin uses) — there is no "SUPER_ADMIN" role string. Keying
    // the privileged menu on user_role_id keeps the sidebar and the route
    // guard in agreement.
    const isSuperAdmin = details?.user_role_id === DEFAULT_SUPER_ADMIN_ROLE_ID;
    if (userRole === "OT_ADMIN" || userRole === "HEALTH_ORG_ADMIN") {
      setSideNavigationMenu(hospitalAdminSideNavigationOptions);
    } else if (userRole === "LABORATORY_ADMIN") {
      setSideNavigationMenu(laboratoryAdminSideNavigationOptions);
    } else if (isSuperAdmin) {
      setSideNavigationMenu(superAdminSideNavigationOptions);
    } else {
      // Unknown/still-loading role: show no privileged nav until the profile
      // confirms super admin, rather than defaulting everyone to it.
      setSideNavigationMenu([]);
    }
  }, [userRole, details]);

  // // useEffect to handle the result of the token refreshing mutation
  // useEffect(() => {
  //   const { status, isSuccess, isError, data, error } = tokenResult;

  //   // Check if the token refreshing is successful
  //   if (status === "fulfilled" && isSuccess) {
  //     const { access } = data.token;

  //     // Calling loginAction dispatch to save the newly generated tokens
  //     if (access) {
  //       dispatch(
  //         loginAction({
  //           accessToken: access,
  //           refreshToken: refreshToken,
  //         })
  //       );
  //     }
  //   } else if (status === "rejected" && isError) {
  //     // Show toast notification for error message
  //     toast((error as ErrorMessage).data?.detail);

  //     // Perform a logout if there is an error refreshing the token
  //     handleLogout();
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [tokenResult]);

  // useEffect to handle the result of the logout mutation
  // useEffect(() => {
  //   const { status, isSuccess, isError, error } = logoutResult;
  //   // Check if the logging out is successful
  //   if (status === "fulfilled" && isSuccess) {
  //     // navigate to the login page if logged out successfully
  //     navigate("/login");
  //   } else if (status === "rejected" && isError) {
  //     // Show toast notification for error message
  //     toast((error as ErrorMessage).data?.detail);
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [logoutResult]);

  const getRouteName = (pathname: string) => {
    // Remove leading/trailing slashes
    const name = pathname.split("/")[1];
    // Split by dash or underscore, capitalize each word, join with spaces
    return name
      .split(/[-_]/)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  }

  useEffect(() => {
    const arrangeHeader = (detailsToUse: UserProfile) => {
      const arr: string[] = pathname.split("/");
      arr.shift();

      // set the user details
      setUserName(
        `${detailsToUse?.given_name ?? ""} ${detailsToUse?.surname ?? ""}`.trim()
      );
      setIsHistoryAvailable(false);

      if (pathname?.includes("home")) {
        // if the route is home page
        setCurrentRouteName(`Welcome back, ${detailsToUse?.given_name ?? ""}`);
      } else if (isAbsolutePath.hasOwnProperty(pathname)) {
        // if its an absolute path
        if (pathname?.includes("-")) {
          setCurrentRouteName(getRouteName(pathname))
        } else {
          setCurrentRouteName(isAbsolutePath[pathname]);
        }
        setOpen(true);
        if (arr.length > 2) {
          setIsHistoryAvailable((prev) => !prev);
        }
      } else {
        // if it contain any more prop in the url show the back button
        if (arr.length > 1) {
          setIsHistoryAvailable((prev) => !prev);
        }
        if (pathname?.includes("-")) {
          setCurrentRouteName(getRouteName(pathname))
        } else {
          const fallbackRouteName =
            arr[0].charAt(0).toUpperCase() + arr[0].slice(1);
          // Prefer a provided label when available, otherwise fall back to inferred route name.
          setCurrentRouteName(state?.label ?? fallbackRouteName);
        }
        setOpen(false);
      }
    };

    if (!isEmpty(details)) {
      arrangeHeader(details as UserProfile);
      if (userDetails) {
        dispatch(setUserDetails({ userDetails: userDetails }));
      }
      if (!hasReferenceData) {
        triggerReferenceData({});
      }
    }
  }, [
    details,
    state,
    pathname,
    dispatch,
    triggerReferenceData,
    userDetails,
    referenceData,
    hasReferenceData,
  ]);

  useEffect(() => {
    if (!isEmpty(referenceData)) {
      dispatch(setReferenceData(referenceData));
    }
  }, [referenceData]);

  const stringAvatar = (name: string) => {
    return {
      children: `${name.split(" ")[0][0]}${name.split(" ")[1][0]}`,
    };
  }

  // Render the layout component
  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <AppBar position="fixed" sx={styles.appBar}>
        <Toolbar
          sx={{
            height: "70px",
            justifyContent: "space-between",
          }}
        >
          <Grid display="flex" alignItems={"center"}>
            {isHistoryAvailable && (
              <IconButton onClick={() => navigate(-1)} aria-label="delete">
                <ArrowBackIcon fontSize="medium" sx={{ color: "#000000" }} />
              </IconButton>
            )}
            {currentRouteName && (
              <Typography
                variant="h5"
                noWrap
                sx={{
                  color: "#262626",
                  paddingRight: 2,
                }}
                component="div"
                fontWeight={"500"}
              >
                {currentRouteName}
              </Typography>
            )}
          </Grid>

          <Typography noWrap component="div" sx={styles.header}>
            <Box component="div" sx={{ marginRight: "10px" }}>
              {userName && (
                <Avatar
                  {...stringAvatar(userName)}
                  sx={{
                    backgroundColor: colors.primary,
                    fontSize: "16px",
                    width: 36,
                    height: 36,
                  }}
                />
              )}
            </Box>
          </Typography>
        </Toolbar>
      </AppBar>

      <Drawer
        sx={{
          width: drawerWidth,
          flexShrink: 0,

          "& .MuiDrawer-paper": {
            width: drawerWidth,
            boxSizing: "border-box",
            backgroundColor: colors.primary,
            justifyContent: "space-between",
          },
          // Kill the browser's native blue tap-highlight on menu links/buttons
          // and replace focus feedback with the drawer's own subtle highlight.
          "& .MuiDrawer-paper a, & .MuiDrawer-paper .MuiListItemButton-root": {
            WebkitTapHighlightColor: "transparent",
          },
          "& .MuiDrawer-paper a:focus-visible, & .MuiDrawer-paper .MuiListItemButton-root:focus-visible":
          {
            // Visible focus indicator: cream outline on the dark drawer meets
            // WCAG 2.4.11 (~15:1); the faint background alone was ~1.1:1.
            outline: `2px solid ${colors.white}`,
            outlineOffset: "-2px",
            borderRadius: "8px",
            backgroundColor: colors.hightlight,
          },
        }}
        variant="permanent"
        anchor="left"
      >
        <Box>
          <Box
            component="a"
            padding={"18px 24px"}
            sx={{ alignItems: "center", display: "flex", justifyContent: "center" }}
          >
            <Logo width={90} height={90} />
          </Box>
          <Box maxHeight={"calc(100vh - 160px)"} overflow={"auto"}>
            {isSidebarLoading ? (
              // Session exists but the profile (which decides super-admin) is
              // still loading — show a loader instead of an empty drawer so the
              // menu doesn't flicker in.
              <Box
                data-testid="sidebar-loader"
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  minHeight: 200,
                }}
              >
                <Loader />
              </Box>
            ) : (
            <List disablePadding>
              {sideNavigationMenu.map((item, index) => (
                <Box key={item.id}>
                  <NavLink
                    key={item.id}
                    to={item.pathLocation}
                    onClick={(ev) => {
                      if (item.id === "logout") {
                        ev.preventDefault();
                        handleLogout();
                      } else if (!isEmpty(item?.subItemMenu)) {
                        setOpen(!open);
                      }
                    }}
                    style={({ isActive }) => {
                      return {
                        backgroundColor: isActive
                          ? isEmpty(item?.subItemMenu)
                            ? colors.hightlight
                            : ""
                          : "",
                        textDecoration: "none",
                        display: "flex",
                        margin: "0 8px",
                        borderRadius: "8px",
                        alignItems: "center",
                        padding: "5px 10px",
                      };
                    }}
                  >
                    <ListItemButton sx={{ paddingY: "16px" }}>
                      <ListItemIcon sx={{ minWidth: "40px" }}>
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText
                        sx={styles.sideMenu}
                        primary={item.navItem}
                      />
                    </ListItemButton>
                    {!isEmpty(item?.subItemMenu) &&
                      (open ? (
                        <ExpandLess
                          sx={{ height: 30, width: 30, color: "primary.light" }}
                        />
                      ) : (
                        <ExpandMore
                          sx={{ height: 30, width: 30, color: "primary.light" }}
                        />
                      ))}
                  </NavLink>
                  <Collapse in={open} timeout="auto" unmountOnExit>
                    {item?.subItemMenu?.map(
                      (
                        subItem: {
                          pathLocation: To;
                          icon:
                          | string
                          | number
                          | boolean
                          | ReactElement<
                            any,
                            string | JSXElementConstructor<any>
                          >
                          | Iterable<ReactNode>
                          | ReactPortal
                          | null
                          | undefined;
                          navItem:
                          | string
                          | number
                          | boolean
                          | ReactElement<
                            any,
                            string | JSXElementConstructor<any>
                          >
                          | Iterable<ReactNode>
                          | ReactPortal
                          | null
                          | undefined;
                        },
                        subItemIndex: Key | null | undefined
                      ) => (
                        <NavLink
                          key={`${item.id}-${String(subItem.pathLocation)}`}
                          to={subItem.pathLocation}
                          style={({ isActive }) => {
                            return {
                              backgroundColor: isActive
                                ? colors.hightlight
                                : "",
                              textDecoration: "none",
                              display: "block",
                              marginLeft: "40px",
                              marginRight: "20px",
                              borderRadius: "8px",
                            };
                          }}
                        >
                          <ListItemButton
                            key={subItemIndex}
                            sx={{ paddingY: "16px" }}
                          >
                            <ListItemIcon sx={{ minWidth: "40px" }}>
                              {subItem.icon}
                            </ListItemIcon>
                            <ListItemText
                              sx={styles.sideMenu}
                              primary={subItem.navItem}
                            />
                          </ListItemButton>
                        </NavLink>
                      )
                    )}
                  </Collapse>
                </Box>
              ))}
            </List>
            )}
          </Box>
        </Box>
        {!isSidebarLoading && (
          <NavLink
            to={"/logout"}
            onClick={(ev) => {
              ev.preventDefault();
              handleLogout();
            }}
            style={({ isActive }) => {
              return {
                backgroundColor: isActive ? colors.hightlight : "",
                textDecoration: "none",
                display: "flex",
                margin: "0 8px",
                borderRadius: "8px",
                alignItems: "center",
                padding: "5px 10px",
              };
            }}
          >
            <ListItemButton sx={{ paddingY: "16px" }}>
              <ListItemIcon sx={{ minWidth: "40px" }}>
                <Logout width={25} height={25} />
              </ListItemIcon>
              <ListItemText sx={styles.sideMenu} primary={"Log out"} />
            </ListItemButton>
          </NavLink>
        )}
      </Drawer>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          bgcolor: "background.default",
          paddingY: 3,
          paddingX: 2,
          marginTop: "45px",
          minHeight: "calc(100vh - 45px)",
          position: "relative",
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
};

export default BasicLayout;
